/**
 * A long opaque string, shown so it can be read, selected and copied (#1244).
 *
 * Exists because the emailed enrollment link is not reliable end-to-end: Gmail
 * rewrites every href to `google.com/url?q=…`, and iOS matches a Universal Link
 * against the *initial* URL of a navigation — so a tap inside Gmail lands in the
 * browser and the app is never offered the link. Copy-and-paste into the app's
 * manual-entry field is the one route that works in every mail client, so the
 * payload has to be presentable as *text*, not only as a link or a QR.
 *
 * That reverses an earlier #1244 E4 decision ("never render the join URL as
 * copyable text — a URL on the clipboard is one sync away from leaving the
 * workshop"). The reversal is deliberate and narrow: the clipboard risk is real but
 * small next to a flow that does not work at all, and the payload is already in the
 * recipient's mailbox. It stays **opt-in** per call site, so a 90-second login code
 * does not get a copy button just because an invitation needed one.
 *
 * Strings come in as props rather than from a namespace, so `settings`- and
 * `common`-scoped callers can both use it without this file picking a namespace.
 */

import { useState } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useNotify } from '@/hooks/useNotify';

interface CopyableCodeProps {
  /** The string to show and copy. Rendered verbatim. */
  value: string;
  /** Tooltip + aria-label for the copy button. */
  copyLabel: string;
  /** Confirmation shown after a successful copy. */
  copiedLabel: string;
  /** Optional line above the box explaining what to do with it. */
  helperText?: string;
}

export function CopyableCode({ value, copyLabel, copiedLabel, helperText }: CopyableCodeProps) {
  const notify = useNotify();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      notify.success(copiedLabel);
      setTimeout(() => { setCopied(false); }, 2000);
    } catch {
      // No clipboard permission (or an insecure context). The value is on screen
      // and selectable, so the user can still copy it by hand — which is why it is
      // rendered as text rather than hidden behind the button.
    }
  };

  return (
    <Stack spacing={0.5} sx={{ width: '100%' }}>
      {helperText && (
        <Typography variant="caption" color="text.secondary">
          {helperText}
        </Typography>
      )}
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box
          sx={{
            flex: 1,
            p: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '0.7rem',
            lineHeight: 1.5,
            // The payload runs to several hundred characters, so it must wrap
            // rather than overflow — and mid-string, since it has no spaces.
            wordBreak: 'break-all',
            // Bounded: at ~830 characters the untruncated payload would otherwise
            // push everything below it off a phone screen.
            maxHeight: 96,
            overflowY: 'auto',
            userSelect: 'all',
          }}
        >
          {value}
        </Box>
        <Tooltip title={copied ? copiedLabel : copyLabel}>
          <IconButton onClick={() => void handleCopy()} size="small" aria-label={copyLabel}>
            {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
}
